import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import ButtonFilled from "../../components/FormElements/ButtonFilled";
import clientsActions from "../../store/clientsStore/clientsActions";
import Loader from "../../components/Loader";
import { useCurrentBranch } from "../../hooks/useCurrentBranch";
import styles from './Clients.module.scss';
import SendNotificationModal from "./components/SendNotificationModal";
import ImportUserModal from "./components/ImportUserModal";
import {convertDataToTime, convertDateToLongString} from "../../tools/convertDateToString";
import branchApi from "../../store/branchStore/branchApi";
import clientsApi from "../../store/clientsStore/clientsApi";
import Dropzone, {useDropzone} from 'react-dropzone';

const modalStyles = {
    overlay: {
        backgroundColor: 'rgba(0,0,0,0.5)',
    },
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        border: 'none',
        padding: 0,
        borderRadius: 24,
    }
};

const DownloadClientsDatabase = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const [isUploadingUsers, setIsUploadingUsers] = useState(false)
    const { currentBranch } = useCurrentBranch();
    const dispatch = useDispatch();
    const { info } = useSelector(s => s.branchReducer);
    const [uploads, setUploads] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [sendModal, setSendModal] = useState(false);
    const [importUserModal, setImportUserModal] = useState(false);
    const timer = useRef(null);
    const onUploadFile = async (files) => {
      if (!currentBranch?.id) return
      try {
        setIsUploadingUsers(true)
        const formData = new FormData()
        formData.append('file', files[0]);
        await branchApi.branchesCustomersUploadUsers(currentBranch.id, formData)
      } finally {
        setIsUploadingUsers(false)
      }
    }
  const {getRootProps, getInputProps} = useDropzone({
    accept: 'application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream',
    maxFiles: 1,
    onDrop: onUploadFile
  });


    const fetchUploads = async (showLoader = false) => {
        if (!info?.id) return;
        try {
            if (showLoader) {
                setIsLoading(true);
            }
            const response = await clientsApi.getCustomersGetUploadsXLSX(info.id);
            const uploads = response.data || [];
            setUploads(uploads);
        } catch (error) {
            console.error('Ошибка при загрузке истории загрузок:', error);
        } finally {
            // Always set loading to false after the first fetch
            setIsLoading(false);
        }
    };

    const debounced = _.debounce(() => fetchUploads(false), 1500);

    useEffect(() => {
        if (info && info.id) {
            // Загрузить данные сразу при входе на страницу с показом лоадера только в первый раз
            fetchUploads(true);
            
            // Затем установить таймер для обновления каждые 5 секунд без лоадера
            if (!timer.current) {
                timer.current = setInterval(() => {
                    fetchUploads(false);
                }, 5000);
            }
        }
        return () => {
            debounced.cancel();
            if (timer.current) {
                clearInterval(timer.current);
                timer.current = null;
            }
        }
    }, [info]);


    const renderStatusBadge = (upload) => {
        const { status, completed_at } = upload;
        
        // Если статус completed - завершено успешно
        if (status === 'completed') {
            return (
                <span className={`${styles.statusBadge} ${styles.statusSuccess}`}>
                    Завершено
                </span>
            );
        }
        
        // Если нет completed_at, значит еще обрабатывается
        if (!completed_at) {
            return (
                <span className={`${styles.statusBadge} ${styles.statusProcessing}`}>
                    Обрабатывается
                </span>
            );
        }
        
        // Иначе это ошибка
        return (
            <span className={`${styles.statusBadge} ${styles.statusError}`}>
                Ошибка
            </span>
        );
    };

    const renderTable = () => {
        if (isLoading && !uploads.length) {
            return (
                <div className={styles.loadingState}>
                    <Loader color="#73D389" size={40} />
                    <p className={styles.loadingText}>Загрузка истории загрузок...</p>
                </div>
            );
        }

        return (
            <div className={styles.tableContainer}>
                <table className={`table ${styles.uploadsTable}`}>
                    <thead>
                    <tr>
                        <th>Статус</th>
                        <th>Обработано строк</th>
                        <th>Всего строк</th>
                        <th>Дата и время начала</th>
                        <th>Дата и время завершения</th>
                        <th>Действия</th>
                    </tr>
                    </thead>
                    <tbody>
                    {uploads.map((upload, index) => (
                        <tr
                          key={upload.upload_id || index}
                          className={styles.tableRow}
                        >
                            <td>{renderStatusBadge(upload)}</td>
                            <td className={styles.numberCell}>{upload.valid_rows || 0}</td>
                            <td className={styles.numberCell}>{upload.total_rows || 0}</td>
                            <td className={styles.dateCell}>
                                {convertDateToLongString(upload.created_at)} {convertDataToTime(upload.created_at)}
                            </td>
                            <td className={styles.dateCell}>
                                {upload.completed_at 
                                    ? `${convertDateToLongString(upload.completed_at)} ${convertDataToTime(upload.completed_at)}`
                                    : <span className={styles.processingText}>В процессе...</span>
                                }
                            </td>
                            <td>
                                <button 
                                    className={styles.detailsButton}
                                    onClick={() => history.push(`/download/clients-database/details/${upload.upload_id}`)}
                                >
                                    Подробнее
                                </button>
                            </td>
                        </tr>
                    ))}
                    {!uploads.length && !isLoading && (
                        <tr>
                            <td colSpan="6" className={styles.emptyState}>
                                <div className={styles.emptyStateContent}>
                                    <p>История загрузок пуста</p>
                                    <p>Загрузите файл для появления записей</p>
                                </div>
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>
        )
    }

    return (
      <div className={styles.container}>
          <div className={styles.header}>
              <div className={styles.instructionsSection}>
                  <h2 className={styles.instructionsTitle}>Инструкции по загрузке файла</h2>
                  <div className={styles.instructionsContent}>
                      <p>Загружаемый файл должен быть в формате <strong>.xlsx</strong> и содержать следующие колонки:</p>
                      <ul>
                          <li><strong>phone</strong> (обязательно) — номер телефона</li>
                          <li><strong>first_name</strong> (опционально) — имя</li>
                          <li><strong>last_name</strong> (опционально) — фамилия</li>
                          <li><strong>birthdate</strong> (опционально) — дата рождения в формате ДД-ММ-ГГГГ</li>
                          <li><strong>balance</strong> (опционально) — баланс</li>
                      </ul>
                      <p className={styles.instructionsNote}>
                          Убедитесь, что заголовки колонок в файле точно соответствуют указанным именам.
                      </p>
                  </div>
              </div>

              <div className={styles.headerTop}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginLeft: 'auto', flexShrink: 0, width: 'max-content' }}>
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0, width: 'max-content', position: 'relative' }}>
                          <div {...getRootProps({style: {flexShrink: 0}})} className={styles.button}>
                            <input {...getInputProps()} />
                            {t('importDatabase')}
                          </div>
                      </div>
                  </div>
              </div>
          </div>
          {uploads.length > 0 && <p>Общее количество загрузок: {uploads.length}</p>}
          <div style={{position: 'relative'}}>
              {renderTable()}
          </div>

          <SendNotificationModal
            ariaHideApp={false}
            isOpen={sendModal}
            style={modalStyles}
            onRequestClose={() => setSendModal(false)}
          />
          <ImportUserModal
            ariaHideApp={false}
            isOpen={importUserModal}
            style={modalStyles}
            onRequestClose={() => setImportUserModal(false)}
          />
      </div>
    )
}

export default DownloadClientsDatabase;
